import {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { BookingFlowOverviewContext } from '~/context/BookingFlowOverviewProvider';
import FormGroup from '~/components/Form/FormGroup';
import utilStyles from '~/styles/utils.module.css';
import overviewStyles from '~/styles/bookingFlow/overview.module.css';
import useTranslation from '~/hooks/useTranslation';
import RoomTypes from '~/components/BookingFlow/Overview/RoomTypes';
import ExtraDays from '~/components/BookingFlow/Overview/ExtraDays';
import OrderBlocksWrapper from '~/components/BookingFlow/Overview/OrderBlocksWrapper';
import Spinner from '~/components/General/Spinner';
import useOptionMapper from '~/hooks/bookingFlow/useOptionMapper';
import SavingIndicator from '~/components/General/SavingIndicator';
import useFormattedRoomTypes from '~/hooks/bookingFlow/useFormattedRoomTypes';
import Button from '~/components/General/Button';
import Alert from '~/components/General/Alert';
import OrderBlock from '~/components/BookingFlow/Overview/OrderBlock';
import bookingFlowRoutes from '~/routes/bookingFlow';
import Stays from '~/components/BookingFlow/Overview/Stays';
import useOverviewFormHooks from '~/hooks/bookingFlow/useOverviewFormHooks';
import scrollToTop from '~/helpers/scrollTo';
import IconArrowRight from '~icons/tabler/arrow-right';
import IconUsers from '~icons/tabler/users';
import IconUser from '~icons/tabler/user';
import SaveTrip from '../Forms/SaveTrip';
import QuotationForm from '../Forms/QuotationForm';
import BookingFlowLayout from '../Layout/Layout';

function BookingFlowOverview() {
  const formContext = useContext(BookingFlowOverviewContext);
  const translate = useTranslation();
  const navigate = useNavigate();
  const { optionGroupsForOrderBlock } = useOptionMapper(
    formContext.overviewFormValue?.order_lines || [],
  );
  const {
    onUpdateRoomTypes,
    onUpdateExtraDays,
    onUpdatePrestaySelected,
    onUpdatePoststaySelected,
    onUpdateOptionQuantity,
  } = useOverviewFormHooks(formContext);
  const { formatRoomTypes } = useFormattedRoomTypes();
  const [roomTypesValid, setRoomTypesValid] = useState(false);

  const onNextStep = useCallback(async () => {
    // If there're errors, we can't continue to the next step.
    if (formContext.formErrors.length > 0) {
      formContext.setShowFormErrors(true);
      return;
    }

    scrollToTop();

    navigate(bookingFlowRoutes.INFORMATION);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formContext.formErrors.length, navigate]);

  const submitButton = useMemo(() => {
    if (!roomTypesValid) {
      return null;
    }

    return (
      <Button
        onClick={onNextStep}
        className={overviewStyles.submitButton}
      >
        {translate('bookingFlow.continue_to_information')}
        <IconArrowRight />
      </Button>
    );
  }, [translate, onNextStep, roomTypesValid]);

  const sidebarActions = useMemo(() => {
    return (
      <>
        {formContext.overviewFormValue?.session_id && (
          <div className={overviewStyles.buttons}>
            <SaveTrip
              className={overviewStyles.saveTrip}
              sessionId={formContext.overviewFormValue?.session_id}
            />
            <QuotationForm
              className={overviewStyles.quotationForm}
              sessionId={formContext.overviewFormValue?.session_id}
            />
          </div>
        )}

        {submitButton}
      </>
    );
  }, [submitButton, formContext.overviewFormValue?.session_id]);

  // Some data is essential to render the form properly, we wait for that response.
  if (!formContext.overviewFormValue?.room_types) {
    return <Spinner />;
  }

  const orderBlocks = formContext.overviewFormValue.order_blocks
    ? formContext.overviewFormValue.order_blocks
    : [];

  return (
    <BookingFlowLayout
      step={0}
      sidebarVisible
      showDisclaimer
      sidebarActions={sidebarActions}
      contentActions={submitButton}
      summary={formContext.overviewFormValue?.summary}
    >
      <h1 className={utilStyles.srOnly}>
        {translate('bookingFlow.steps.my_trip')}
      </h1>

      {formContext.isFormLoading && (
        <SavingIndicator />
      )}

      {formContext.formErrors.length > 0 && formContext.showFormErrors && (
        <Alert
          type="danger"
          icon="alert-triangle"
          focusOnRender
        >
          <ul>
            {formContext.formErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {
        formContext.overviewFormValue?.notifications
        && formContext.overviewFormValue.notifications.length > 0
        && (
          <Alert
            type="info"
            icon="alert-triangle"
          >
            <ul>
              {formContext.overviewFormValue.notifications.map((notification) => (
                <li key={notification.message}>{notification.message}</li>
              ))}
            </ul>
          </Alert>
        )
      }

      <FormGroup>
        <h2 className={overviewStyles.personsTitle}>
          {formContext.overviewFormValue.pax > 1 ? <IconUsers /> : <IconUser />}
          {translate('bookingFlow.overview.persons')}
        </h2>

        <div className={overviewStyles.personsValue}>
          {formContext.overviewFormValue.pax}
          {' '}
          {translate(formContext.overviewFormValue.pax === 1 ? 'bookingFlow.overview.pax' : 'bookingFlow.overview.pax_plural')}
        </div>
      </FormGroup>

      {formContext.overviewFormValue.room_types && (
        <FormGroup>
          <RoomTypes
            value={formContext.overviewFormValue.room_types_selected || []}
            roomTypes={formatRoomTypes(formContext.overviewFormValue.room_types)}
            pax={formContext.overviewFormValue.pax}
            onChange={onUpdateRoomTypes}
            onValidityChange={(valid) => setRoomTypesValid(valid)}
          />
        </FormGroup>
      )}

      {roomTypesValid && (
        <FormGroup>
          <OrderBlocksWrapper
            preStays={(
              <Stays
                stays={formContext.overviewFormValue.prestays || []}
                onUpdateStaySelected={onUpdatePrestaySelected}
              />
            )}
            postStays={(
              <Stays
                stays={formContext.overviewFormValue.poststays || []}
                onUpdateStaySelected={onUpdatePoststaySelected}
              />
            )}
            extraDays={(
              <ExtraDays
                value={formContext.overviewFormValue.extra_days || []}
                onChange={onUpdateExtraDays}
              />
            )}
          >
            {orderBlocks.map((orderBlock) => {
              const optionGroups = optionGroupsForOrderBlock(orderBlock.id);

              return (
                <OrderBlock
                  key={orderBlock.id}
                  orderBlock={orderBlock}
                  optionGroups={optionGroups}
                  pax={formContext.overviewFormValue?.pax ?? 0}
                  onUpdateOptionQuantity={onUpdateOptionQuantity}
                />
              );
            })}
          </OrderBlocksWrapper>
        </FormGroup>
      )}
    </BookingFlowLayout>
  );
}

export default BookingFlowOverview;
